// Description: Component that will render one input based on the type
// Updated: 01/07/2025
// Author: Simon Franklin

// React
import React from 'react'

// Material UI Component
import { Box, createTheme, ThemeProvider, Typography, TextField, FormControl, Select, MenuItem } from '@mui/material'

// Components and Functions for the project
import { AlertIcon } from "../../pages/Integrations/AddIntegration";
import themeColors from '../ThemeColors/ThemeColors';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

const startDateTheme = createTheme({
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: '#F8F9FB',
                    color: themeColors.gray30,
                    borderRadius: '8px',
                    border: "1px solid #E8E8E8"
                },
            },
        },
        MuiPickersDay: {
            styleOverrides: {
                root: {
                    color: themeColors.purple400,
                    borderRadius: '2px',
                    borderWidth: '1px',
                    border: '1px solid',
                    backgroundColor: '#FFF',
                    '&:hover': {
                        backgroundColor: themeColors.purple400,
                        color: '#FFF',
                    },
                    '&.Mui-selected': {
                        backgroundColor: themeColors.purple400 + " !important",
                        color: '#FFF !important',
                    },
                }
            }

        }

    }
});

// Render The Component
const CustomDynamicInput = ({ field, formState, setFormState }) => {

    function getLabel(key) {
        switch (key) {
            case "data_vencimento_inicio":
                return "Data de Vencimento (Início)";
            case "data_vencimento_fim":
                return "Data de Vencimento (Fim)";
            case "status":
                return "Status";
            case "busca":
                return "Busca";
            case "valor_busca":
                return "Valor da Busca";
            case "tipo_data":
                return "Tipo de Data";
            case "data_inicio":
                return "Data de Início";
            case "data_fim":
                return "Data de Fim";
            case "tipo_resultado":
                return "Tipo de Resultado";
            case "apenas_quitado":
                return "Status da Fatura";
            case "apenas_em_aberto":
                return "Apenas em Aberto";
            case "valor_zerado":
                return "Valor Zerado";
            case "exibir_pix_copia_cola":
                return "Exibir Pix Copia e Cola";
            case "exibir_fatura_inativa":
                return "Exibir Fatura Inativa";
            case "exibir_faturas_renegociacao":
                return "Exibir Faturas Renegociação";
            case "status_cadastro_cliente":
                return "Status do Cadastro do Cliente";
            case "campos":
                return "Campos Extras";
            case "cliente_id":
                return "ID do Cliente";
            case "cpfcnpj":
                return "CPF ou CNPJ";
            case "plano":
                return "Plano";
            case "contrato":
                return "Contrato";
            case "portador":
                return "Portador";
            case "contrato_status":
                return "Status do Contrato";
            case "tipo_servico":
                return "Tipo de Serviço";
            case "title":
                return "Nome";
            case "desc":
                return "Descrição";
            default:
                return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
        }
    };

    const dateFields = ["data_inicio", "data_fim", "data_vencimento_inicio", "data_vencimento_fim"];

    function renderInput(label) {
        const isDateField = dateFields.includes(label);

        const commonSelectStyle = {
            height: "48px",
            backgroundColor: "#F8F9FB",
            borderRadius: "8px",
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E8E8E8",
            },
            padding: "0 24px",
            border: "1px solid #E8E8E8",
        };

        const renderStatusInvoiceSelect = () => (
            <FormControl fullWidth>
                <Select
                    value={formState.apenas_quitado}
                    onChange={(e) => {
                        const value = e.target.value;

                        if (value === "sim") {
                            setFormState((prev) => ({
                                ...prev,
                                "apenas_quitado": "sim",
                                "apenas_em_aberto": "nao"
                            }));
                        } else {
                            setFormState((prev) => ({
                                ...prev,
                                "apenas_quitado": "nao",
                                "apenas_em_aberto": "sim"
                            }));
                        }
                        
                    }}
                    sx={commonSelectStyle}
                >
                    <MenuItem value="sim">Apenas Quitado</MenuItem>
                    <MenuItem value="nao">Apenas em Aberto</MenuItem>
                </Select>
            </FormControl>
        );

        const renderStatusSelect = () => (
            <FormControl fullWidth>
                <Select
                    value={formState[label] || ""}
                    onChange={(e) =>
                        setFormState((prev) => ({
                            ...prev,
                            [label]: e.target.value,
                        }))
                    }
                    sx={commonSelectStyle}
                >
                    <MenuItem value="aberto">Aberto</MenuItem>
                    <MenuItem value="pago">Pago</MenuItem>
                </Select>
            </FormControl>
        );

        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "32px" }}>
                <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <Typography sx={{ textTransform: "uppercase", fontSize: "17px", fontWeight: 600 }}>
                        {getLabel(label)}
                    </Typography>
                    <AlertIcon />
                </Box>

                {isDateField ? (
                    <ThemeProvider theme={startDateTheme}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                            <DesktopDatePicker
                                inputFormat="DD/MM/YYYY"
                                value={formState[label] ? dayjs(formState[label]) : null}
                                onChange={(newValue) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        [label]: newValue,
                                    }))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        sx={{
                                            backgroundColor: "#F8F9FB",
                                            borderRadius: "8px",
                                            "& .MuiOutlinedInput-root": {
                                                padding: "0",
                                            },
                                            "& input": {
                                                height: "48px",
                                            },
                                            ".css-12qs7hp-MuiInputBase-input-MuiOutlinedInput-input": {
                                                padding: "17px 24px",
                                                paddingRight: 0,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </ThemeProvider>
                ) : label === "apenas_quitado" ? (
                    renderStatusInvoiceSelect()
                ) : label === "status" ? (
                    renderStatusSelect()
                ) : (
                    <input
                        onChange={(e) =>
                            setFormState((prev) => ({
                                ...prev,
                                [label]: e.target.value,
                            }))
                        }
                        type={label === "password" ? "password" : "text"}
                        name={getLabel(label)}
                        placeholder={getLabel(label)}
                        value={formState[label] || ""}
                        style={{
                            height: "48px",
                            width: "100%",
                            borderRadius: "8px",
                            border: "1px solid #E8E8E8",
                            backgroundColor: "#F8F9FB",
                            padding: "17px 24px",
                            outline: "none",
                        }}
                    />
                )}
            </Box>
        );
    }

    return (
        <>
            {renderInput(field)}
        </>
    )
}

export default CustomDynamicInput